import React, { useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Play, Eye, RefreshCw, ArrowDown, ArrowUp, X, Printer, Download } from 'lucide-react';
import { toast } from 'sonner';

const fmtINR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return sign + '₹' + Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};
const fmtINRPrec = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};
const fmtQty = (n) => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 4 });

const ImportHoldingsSnapshot = ({ user, setUser }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [tab, setTab] = useState('qty_mismatch');
  const [opts, setOpts] = useState({ update_qty: true, update_price: true, update_category: true, create_missing: false });

  const upload = useCallback(async () => {
    if (!file) { toast.error('Choose an Excel file first'); return; }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/imports/holdings-snapshot/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setPreview(res.data);
      const s = res.data.summary;
      toast.success(`Parsed ${s.file_rows_parsed} rows · ${s.qty_mismatch} qty mismatches · ${s.only_in_file} only in file`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse file');
    } finally { setLoading(false); }
  }, [file]);

  const apply = useCallback(async () => {
    if (!file) { toast.error('No file selected'); return; }
    if (!window.confirm(`Apply changes? This will:\n${opts.update_qty ? '• update quantities\n' : ''}${opts.update_price ? '• update prices\n' : ''}${opts.update_category ? '• update categories\n' : ''}${opts.create_missing ? '• create new holdings\n' : ''}\nThis cannot be auto-undone.`)) return;
    try {
      setApplying(true);
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(opts).forEach(([k,v]) => fd.append(k, String(v)));
      const res = await api.post('/imports/holdings-snapshot/apply', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const { updated, created, skipped } = res.data;
      toast.success(`Applied: ${updated} updated · ${created} created · ${skipped} unchanged`);
      // re-preview to show new diffs
      await upload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  }, [file, opts, upload]);

  const s = preview?.summary;
  const tabRows = preview?.[tab] || [];

  // Build CSV from current tab rows
  const downloadCSV = useCallback(() => {
    if (!tabRows.length) { toast.error('Nothing to export'); return; }
    const tabLabel = { qty_mismatch: 'Qty mismatch', price_drift: 'Price drift', only_in_file: 'Only in file', only_in_app: 'Only in app' }[tab] || tab;
    let headers, rows;
    if (tab === 'qty_mismatch' || tab === 'price_drift') {
      headers = ['Member','Excel Symbol','App Ticker','Yahoo NSE','Yahoo BSE','Excel Qty','App Qty','Δ Qty','Excel Price','App Price','Δ %','Excel Value','App Value','Δ Value'];
      rows = tabRows.map(r => [
        r.member_name, r.symbol,
        r.app_ticker || '', r.yahoo_nse || '', r.yahoo_bse || '',
        r.file_qty, r.app_qty, r.delta_qty,
        r.file_price, r.app_price, r.price_pct_diff,
        r.file_market_value, r.app_market_value, r.value_delta,
      ]);
    } else if (tab === 'only_in_file') {
      headers = ['Member','Symbol','File Qty','Price','Market Value','Categories'];
      rows = tabRows.map(r => [r.member_name, r.symbol, r.file_qty, r.file_price, r.file_market_value, (r.categories||[]).join('|')]);
    } else {
      headers = ['Member','Symbol','App Ticker','Yahoo NSE','Yahoo BSE','App Qty','App Price','App Market Value','App Category'];
      rows = tabRows.map(r => [
        r.member_name, r.symbol,
        r.app_ticker || '', r.yahoo_nse || '', r.yahoo_bse || '',
        r.app_qty, r.app_price, r.app_market_value, r.app_category,
      ]);
    }
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const str = String(v);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0,10);
    a.download = `reconciliation-${tab}-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${tabRows.length} rows · ${tabLabel}`);
  }, [tab, tabRows]);

  const printTab = useCallback(() => {
    if (!tabRows.length) { toast.error('Nothing to print'); return; }
    document.body.classList.add('printing-reconciliation');
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove('printing-reconciliation'), 500);
    }, 100);
  }, [tabRows]);

  return (
    <Layout user={user} setUser={setUser}>
      <div data-testid="import-holdings-snapshot-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight mb-1 flex items-center gap-2">
            <FileSpreadsheet className="text-emerald-600" size={28} /> Holdings Snapshot Reconciliation
          </h1>
          <p className="text-sm text-gray-600">
            Upload your master Excel sheet (e.g. <code className="text-xs bg-gray-100 px-1 rounded">VMR Family Consolidated Shares.xlsx</code>).
            Compare it side-by-side with the app's current holdings, then optionally sync.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Expected columns: <code>File / Category</code>, <code>Family Member</code>, <code>Stock Symbol</code>, <code>Shares Held</code>, <code>Current Price</code>, <code>Market Value</code>
          </p>
        </div>

        {/* Upload bar */}
        <div className="card p-4 mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              className="text-sm"
              data-testid="file-input"
            />
            <button
              onClick={upload}
              disabled={!file || loading}
              className="btn-primary text-sm flex items-center gap-1 disabled:opacity-50"
              data-testid="preview-btn"
            >
              <Eye size={14} /> {loading ? 'Parsing…' : 'Preview Discrepancies'}
            </button>
            {preview && (
              <button
                onClick={() => { setPreview(null); setFile(null); }}
                className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
                data-testid="clear-btn"
              >
                <X size={12} /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Summary cards */}
        {s && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="summary-cards">
              <div className="card p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">File Total</p>
                <p className="text-xl font-bold text-gray-700">{fmtINR(s.file_total_value)}</p>
                <p className="text-[11px] text-gray-500 mt-1">{s.file_holdings_aggregated} unique holdings · {s.file_rows_parsed} rows</p>
              </div>
              <div className="card p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">App Total</p>
                <p className="text-xl font-bold text-gray-700">{fmtINR(s.app_total_value)}</p>
                <p className="text-[11px] text-gray-500 mt-1">{s.app_holdings_aggregated} holdings in app</p>
              </div>
              <div className={`card p-3 ${s.value_delta >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Δ Value (File − App)</p>
                <p className={`text-xl font-bold flex items-center gap-1 ${s.value_delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {s.value_delta >= 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                  {s.value_delta >= 0 ? '+' : ''}{fmtINR(s.value_delta)}
                </p>
              </div>
              <div className="card p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Reconciliation</p>
                <div className="flex flex-wrap gap-1.5 text-xs mt-1">
                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium" title="Matched">{s.matched} ✓</span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium" title="Qty mismatch">{s.qty_mismatch} qty</span>
                  <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-medium" title="Price drift >5%">{s.price_drift} price</span>
                  <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-medium" title="Only in file">{s.only_in_file} new</span>
                  <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-medium" title="Only in app">{s.only_in_app} extra</span>
                </div>
              </div>
            </div>

            {s.unresolved_members?.length > 0 && (
              <div className="card p-3 mb-5 border-l-4 border-amber-400 bg-amber-50">
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle size={14} /> Unresolved members</p>
                <p className="text-xs text-amber-700 mt-1">
                  These names from the Excel could not be matched to any family member: {s.unresolved_members.join(', ')}.
                  Add them to your family members or fix typos.
                </p>
              </div>
            )}

            {/* Apply controls */}
            <div className="card p-3 mb-5" data-testid="apply-controls">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex flex-wrap gap-3 text-sm">
                  <label className="flex items-center gap-1"><input type="checkbox" checked={opts.update_qty} onChange={e => setOpts({...opts, update_qty: e.target.checked})} /> Update quantities</label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={opts.update_price} onChange={e => setOpts({...opts, update_price: e.target.checked})} /> Update prices</label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={opts.update_category} onChange={e => setOpts({...opts, update_category: e.target.checked})} /> Update categories</label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={opts.create_missing} onChange={e => setOpts({...opts, create_missing: e.target.checked})} /> Create missing holdings</label>
                </div>
                <button
                  onClick={apply}
                  disabled={applying}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 disabled:opacity-50"
                  data-testid="apply-btn"
                >
                  <Play size={14} /> {applying ? 'Applying…' : 'Apply Changes'}
                </button>
              </div>
            </div>

            {/* Diff tabs */}
            <div className="card overflow-hidden" data-testid="diff-card" id="recon-diff-card">
              <div className="flex gap-1 px-3 py-2 border-b bg-gray-50 overflow-x-auto items-center print:hidden">
                {[
                  ['qty_mismatch', `Qty mismatch (${s.qty_mismatch})`],
                  ['price_drift', `Price drift (${s.price_drift})`],
                  ['only_in_file', `Only in file (${s.only_in_file})`],
                  ['only_in_app', `Only in app (${s.only_in_app})`],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${tab === k ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
                    data-testid={`tab-${k}`}
                  >
                    {label}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    onClick={downloadCSV}
                    disabled={!tabRows.length}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium border border-gray-200 bg-white text-gray-700 hover:bg-emerald-50 hover:border-emerald-200 disabled:opacity-50"
                    data-testid="export-csv-btn"
                    title="Download current tab as CSV"
                  >
                    <Download size={13} /> Export CSV
                  </button>
                  <button
                    onClick={printTab}
                    disabled={!tabRows.length}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium border border-gray-200 bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-200 disabled:opacity-50"
                    data-testid="print-btn"
                    title="Print current tab"
                  >
                    <Printer size={13} /> Print
                  </button>
                </div>
              </div>
              {/* Print-only header (replaces tab nav when printing) */}
              <div className="hidden print:block px-4 py-3 border-b">
                <h2 className="text-base font-semibold">
                  Holdings Reconciliation —{' '}
                  {tab === 'qty_mismatch' && `Qty mismatch (${s.qty_mismatch})`}
                  {tab === 'price_drift' && `Price drift (${s.price_drift})`}
                  {tab === 'only_in_file' && `Only in file (${s.only_in_file})`}
                  {tab === 'only_in_app' && `Only in app (${s.only_in_app})`}
                </h2>
                <p className="text-xs text-gray-600 mt-0.5">
                  Generated {new Date().toLocaleString('en-IN')} · File total {fmtINR(s.file_total_value)} · App total {fmtINR(s.app_total_value)} · Δ {fmtINR(s.value_delta)}
                </p>
              </div>

              <div className="overflow-auto max-h-[60vh] print:max-h-none print:overflow-visible">
                {tabRows.length === 0 ? (
                  <p className="py-8 text-center text-gray-500 text-sm">
                    <CheckCircle2 size={20} className="inline-block mr-1 text-emerald-500" /> Nothing here — no records in this category.
                  </p>
                ) : (tab === 'qty_mismatch' || tab === 'price_drift') ? (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Member</th>
                        <th className="text-left px-3 py-2 font-semibold">Excel Symbol</th>
                        <th className="text-left px-3 py-2 font-semibold">App Ticker</th>
                        <th className="text-right px-3 py-2 font-semibold">Excel Qty</th>
                        <th className="text-right px-3 py-2 font-semibold">App Qty</th>
                        <th className="text-right px-3 py-2 font-semibold">Δ Qty</th>
                        <th className="text-right px-3 py-2 font-semibold">Excel Price</th>
                        <th className="text-right px-3 py-2 font-semibold">App Price</th>
                        <th className="text-right px-3 py-2 font-semibold">Δ %</th>
                        <th className="text-right px-3 py-2 font-semibold">Δ Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tabRows.map((r, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50" data-testid={`diff-row-${i}`}>
                          <td className="px-3 py-2 text-gray-700">{r.member_name}</td>
                          <td className="px-3 py-2 font-medium">{r.symbol}</td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {r.app_ticker ? (
                              <span title={`Yahoo: ${r.yahoo_nse || ''} ${r.yahoo_bse || ''}`}
                                className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100">
                                {r.app_ticker}
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right">{fmtQty(r.file_qty)}</td>
                          <td className="px-3 py-2 text-right">{fmtQty(r.app_qty)}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${r.delta_qty === 0 ? 'text-gray-400' : r.delta_qty > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {r.delta_qty > 0 ? '+' : ''}{fmtQty(r.delta_qty)}
                          </td>
                          <td className="px-3 py-2 text-right">{fmtINRPrec(r.file_price)}</td>
                          <td className="px-3 py-2 text-right">{fmtINRPrec(r.app_price)}</td>
                          <td className={`px-3 py-2 text-right ${Math.abs(r.price_pct_diff) > 5 ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
                            {r.price_pct_diff > 0 ? '+' : ''}{r.price_pct_diff?.toFixed?.(2)}%
                          </td>
                          <td className={`px-3 py-2 text-right font-semibold ${r.value_delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {r.value_delta >= 0 ? '+' : ''}{fmtINR(r.value_delta)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : tab === 'only_in_file' ? (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Member</th>
                        <th className="text-left px-3 py-2 font-semibold">Symbol</th>
                        <th className="text-right px-3 py-2 font-semibold">File Qty</th>
                        <th className="text-right px-3 py-2 font-semibold">Price</th>
                        <th className="text-right px-3 py-2 font-semibold">Market Value</th>
                        <th className="text-left px-3 py-2 font-semibold">Categories</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tabRows.map((r, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-700">{r.member_name}</td>
                          <td className="px-3 py-2 font-medium">{r.symbol}</td>
                          <td className="px-3 py-2 text-right">{fmtQty(r.file_qty)}</td>
                          <td className="px-3 py-2 text-right">{fmtINRPrec(r.file_price)}</td>
                          <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{fmtINR(r.file_market_value)}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{r.categories?.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Member</th>
                        <th className="text-left px-3 py-2 font-semibold">Symbol</th>
                        <th className="text-right px-3 py-2 font-semibold">App Qty</th>
                        <th className="text-right px-3 py-2 font-semibold">Price</th>
                        <th className="text-right px-3 py-2 font-semibold">Market Value</th>
                        <th className="text-left px-3 py-2 font-semibold">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tabRows.map((r, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-700">{r.member_name}</td>
                          <td className="px-3 py-2 font-medium">{r.symbol}</td>
                          <td className="px-3 py-2 text-right">{fmtQty(r.app_qty)}</td>
                          <td className="px-3 py-2 text-right">{fmtINRPrec(r.app_price)}</td>
                          <td className="px-3 py-2 text-right text-rose-700 font-semibold">{fmtINR(r.app_market_value)}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{r.app_category}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {!preview && (
          <div className="card p-8 text-center text-gray-500" data-testid="empty-state">
            <Upload size={32} className="mx-auto mb-2 opacity-40" />
            <p>Choose your Excel file above and click <strong>Preview Discrepancies</strong> to see the diff.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ImportHoldingsSnapshot;
